import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "src/lib/utils";

// ── Icons (Bootstrap Icons SVG equivalents) ────────────────────────────────
const InfoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
  </svg>
);

const SuccessIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
    <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z" />
  </svg>
);

const WarningIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
    <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden>
    <path d="M1 1l9 9M10 1L1 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const DEFAULT_ICONS: Record<string, React.ReactNode> = {
  default: <InfoIcon />,
  info: <InfoIcon />,
  success: <SuccessIcon />,
  warning: <WarningIcon />,
  destructive: <ErrorIcon />,
  error: <ErrorIcon />,
};

// ── CVA variants ───────────────────────────────────────────────────────────
// Tailwind's JIT parser splits arbitrary values on `/`, so CSS variables that
// contain a slash (e.g. --info-bg: 227 100% 72% / 0.10) can't be used inside
// bg-[hsl(var(--info-bg))]. We use a [background-color:...] property shorthand
// instead, which bypasses the parser and lets the browser resolve the variable.
const alertVariants = cva(
  "relative flex w-full items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-[13px] leading-relaxed transition-colors duration-150",
  {
    variants: {
      colorVariant: {
        default:
          "bg-muted border-border text-foreground [--alert-icon:hsl(var(--muted-foreground))]",
        // info: icon is accent-colored, but text stays at muted-foreground (not blue)
        info:
          "[background-color:hsl(var(--info-bg))] [border-color:hsl(var(--info)/0.18)] text-muted-foreground [--alert-icon:hsl(var(--info))]",
        // success/warning/destructive: text AND icon are the status color
        success:
          "[background-color:hsl(var(--success-bg))] [border-color:hsl(var(--success)/0.18)] text-[hsl(var(--success))] [--alert-icon:hsl(var(--success))]",
        warning:
          "[background-color:hsl(var(--warning-bg))] [border-color:hsl(var(--warning)/0.18)] text-[hsl(var(--warning))] [--alert-icon:hsl(var(--warning))]",
        destructive:
          "[background-color:hsl(var(--destructive-bg))] [border-color:hsl(var(--destructive)/0.18)] text-[hsl(var(--destructive))] [--alert-icon:hsl(var(--destructive))]",
      },
      styleVariant: {
        standard: "",
        outlined: "bg-transparent",
        filled:   "text-white [--alert-icon:white]",
      },
    },
    compoundVariants: [
      // filled — solid bg, same color border
      { colorVariant: "default",     styleVariant: "filled", class: "bg-muted border-border text-foreground [--alert-icon:hsl(var(--muted-foreground))]" },
      { colorVariant: "info",        styleVariant: "filled", class: "[background-color:hsl(var(--info))] [border-color:hsl(var(--info))]" },
      { colorVariant: "success",     styleVariant: "filled", class: "[background-color:hsl(var(--success))] [border-color:hsl(var(--success))]" },
      { colorVariant: "warning",     styleVariant: "filled", class: "[background-color:hsl(var(--warning))] [border-color:hsl(var(--warning))]" },
      { colorVariant: "destructive", styleVariant: "filled", class: "[background-color:hsl(var(--destructive))] [border-color:hsl(var(--destructive))]" },
      // outlined — transparent bg, keep color border
      { colorVariant: "info",        styleVariant: "outlined", class: "[border-color:hsl(var(--info)/0.35)]" },
      { colorVariant: "success",     styleVariant: "outlined", class: "[border-color:hsl(var(--success)/0.35)]" },
      { colorVariant: "warning",     styleVariant: "outlined", class: "[border-color:hsl(var(--warning)/0.35)]" },
      { colorVariant: "destructive", styleVariant: "outlined", class: "[border-color:hsl(var(--destructive)/0.35)]" },
    ],
    defaultVariants: {
      colorVariant: "default",
      styleVariant: "standard",
    },
  }
);

// ── Types ──────────────────────────────────────────────────────────────────
type AlertColorVariant = "default" | "info" | "success" | "warning" | "destructive" | "error";
type AlertStyleVariant = "standard" | "outlined" | "filled";

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    Omit<VariantProps<typeof alertVariants>, "colorVariant" | "styleVariant"> {
  /** Color variant. "error" is an alias for "destructive". */
  variant?: AlertColorVariant | AlertStyleVariant;
  /** MUI-compatible severity alias — used when variant is a style ("filled" | "outlined" | "standard"). */
  severity?: "error" | "warning" | "info" | "success";
  /** Action element rendered after the body (e.g. an undo button). */
  action?: React.ReactNode;
  /** Override the default icon. Pass `false` to suppress the icon entirely. */
  icon?: React.ReactNode | false;
  /** Renders a close button and calls this handler when clicked. */
  onClose?: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────
const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, severity, action, icon, onClose, children, ...props }, ref) => {
    const isStyleVariant =
      variant === "filled" || variant === "outlined" || variant === "standard";

    const colorVariant = (
      isStyleVariant
        ? severity ?? "default"
        : variant === "error"
        ? "destructive"
        : variant ?? severity ?? "default"
    ) as AlertColorVariant;

    const styleVariant: AlertStyleVariant = isStyleVariant
      ? (variant as AlertStyleVariant)
      : "standard";

    const resolvedIcon =
      icon === false
        ? null
        : icon ?? DEFAULT_ICONS[colorVariant] ?? DEFAULT_ICONS.default;

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          alertVariants({ colorVariant, styleVariant }),
          className
        )}
        {...props}
      >
        {resolvedIcon && (
          <span className="mt-[1px] shrink-0 text-[--alert-icon]">
            {resolvedIcon}
          </span>
        )}

        <div className="min-w-0 flex-1">{children}</div>

        {(action || onClose) && (
          <div className="ml-1 flex shrink-0 items-center gap-1">
            {action}
            {onClose && !action && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Dismiss"
                className="flex h-5 w-5 items-center justify-center rounded opacity-50 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
);
Alert.displayName = "Alert";

// ── Sub-components ─────────────────────────────────────────────────────────
const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-0.5 text-[13px] font-medium leading-snug", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-[12.5px] leading-relaxed opacity-90 [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };